import React from 'react'
import {
	Box,
	Divider,
	Drawer as MuiDrawer,
	List,
	ListItem,
	ListItemAvatar,
	Avatar,
	ListItemText,
	Typography,
	Button,
} from '@mui/material'
import { styled, Theme, CSSObject } from '@mui/material/styles'
import { Navigation } from '@/types/navigation'
import { NavigationItem } from './NavigationItem'

import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED, HEADER_HEIGHT } from './config'
/** ICONS */
import CreditCardIcon from '@mui/icons-material/CreditCard'
import ZapIcon from '@mui/icons-material/FlashOn'
/** POS */
import POS from '@/components/pos'

const openedMixin = (theme: Theme): CSSObject => ({
	width: DRAWER_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `${DRAWER_WIDTH_COLLAPSED}px`,
})

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
	width: DRAWER_WIDTH,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	// --- STYLE CORRECTIONS APPLIED HERE ---
	'& .MuiDrawer-paper': {
		// REPLICATE: Set the custom background color from the original
		backgroundColor: theme.palette.background.secondary,
		// REPLICATE: Hide the scrollbar as in the original
		scrollbarWidth: 'none', // For Firefox
		'&::-webkit-scrollbar': {
			display: 'none', // For Chrome, Safari, and Opera
		},
	},
	// --- END OF CORRECTIONS ---
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': {
			...openedMixin(theme),
			// We also need to re-apply the background color for the open state
			backgroundColor: theme.palette.background.secondary,
		},
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': {
			...closedMixin(theme),
			// And re-apply it for the closed state
			backgroundColor: theme.palette.background.secondary,
		},
	}),
}))

interface NavigationDrawerProps {
	open: boolean
	headerHeight: number
	navigations: Navigation[]
	activePath: string
}

export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({ open, headerHeight, navigations }) => {
	const [isPosOpen, setIsPosOpen] = React.useState(false)

	return (
		<StyledDrawer variant="permanent" open={open}>
			<Box sx={{ height: HEADER_HEIGHT, p: 2, display: 'flex', alignItems: 'center' }}>
				<ListItem sx={{ p: 0 }}>
					<ListItemAvatar>
						<Avatar src={`${process.env.PUBLIC_URL}/homemedia.jpg`} sx={{ width: 30, height: 30 }} />
					</ListItemAvatar>
					{open && <ListItemText primary="ЭН БИ СИ" secondary="Төлбөрийн систем" />}
				</ListItem>
			</Box>
			<Divider />
			<List sx={{ mt: 2 }} component="nav">
				{navigations.map((item) => (
					// CORRECTED LINE: The 'activePath' prop has been removed.
					<NavigationItem key={item.id} item={item} open={open} />
				))}
			</List>
			<Box sx={{ marginTop: 'auto' }} id="sidebar-footer">
				<Box sx={{ p: 2, mt: 'auto' }}>
					<Box
						sx={{
							position: 'relative',
							overflow: 'hidden',
							p: 2,
							borderRadius: '16px', // rounded-2xl
							backgroundColor: (theme) =>
								theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)',
							backdropFilter: 'blur(12px)',
							border: (theme) =>
								theme.palette.mode === 'dark'
									? '1px solid rgba(255, 255, 255, 0.1)'
									: '1px solid rgba(255, 255, 255, 0.5)',
							boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
							transition: 'all 0.3s ease',
							'&:hover .decorative-circle': {
								transform: 'scale(1.5)',
							},
						}}
					>
						{/* Decorative background element */}
						<Box
							className="decorative-circle"
							sx={{
								position: 'absolute',
								top: -16,
								right: -16,
								width: 64,
								height: 64,
								bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#541370' : '#e1bee7'), // purple-200
								borderRadius: '50%',
								opacity: 0.5,
								filter: 'blur(20px)',
								transition: 'transform 0.7s ease',
								zIndex: 0,
							}}
						/>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								mb: 1.5,
								position: 'relative',
								zIndex: 1,
							}}
						>
							<Box
								sx={{
									p: 1,
									display: 'flex',
									background: 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)',
									color: '#7b1fa2', // purple-700
									borderRadius: '12px',
									boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
								}}
							>
								<ZapIcon sx={{ fontSize: 18 }} />
							</Box>
							<Box>
								<Typography
									variant="subtitle2"
									sx={{
										fontWeight: 800,
										color: 'text.primary',
										lineHeight: 1.2,
										fontSize: '0.875rem',
									}}
								>
									POS Terminal
								</Typography>
								<Typography
									variant="caption"
									sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '10px' }}
								>
									Quick Sales Mode
								</Typography>
							</Box>
						</Box>
						{isPosOpen && <POS open={isPosOpen} onClose={() => setIsPosOpen(false)} />}
						<Button
							fullWidth
							variant="contained"
							onClick={() => setIsPosOpen(true)}
							startIcon={<CreditCardIcon sx={{ fontSize: 16 }} />}
							sx={{
								position: 'relative',
								zIndex: 1,
								bgcolor: '#8b2aa0',
								textTransform: 'none',
								fontWeight: 600,
								borderRadius: '12px',
								color: (theme) => theme.palette.getContrastText('#8b2aa0'),
								py: 1,
								boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
								'&:hover': {
									bgcolor: '#7a258c',
									boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
								},
								'&:active': {
									transform: 'scale(0.95)',
								},
								transition: 'all 0.2s ease',
							}}
						>
							POS Нээх
						</Button>
					</Box>
				</Box>
			</Box>
		</StyledDrawer>
	)
}
